import { useState, useEffect, useCallback } from 'react';
import { Shield, User, Trash2, UserCheck, RefreshCw, Send, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  user_approve: { label: 'Approvazione utente', color: '#32D74B', Icon: UserCheck },
  user_reject: { label: 'Rifiuto utente', color: '#FF3B30', Icon: Trash2 },
  user_role_change: { label: 'Cambio ruolo', color: '#0A84FF', Icon: Shield },
  user_delete: { label: 'Eliminazione utente', color: '#FF3B30', Icon: Trash2 },
  request_delete: { label: 'Eliminazione richiesta', color: '#FF3B30', Icon: Trash2 },
  emergency_dispatch: { label: 'Inoltro 112/118', color: '#FF9F0A', Icon: Send },
};

export default function AdminAuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');

  const token = localStorage.getItem('lifelink_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchLog = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterAction ? { action: filterAction, limit: 200 } : { limit: 200 };
      const { data } = await axios.get(`${API}/admin/audit-log`, { headers, params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction]);

  useEffect(() => { fetchLog(); }, [fetchLog]);

  return (
    <div className="space-y-4" data-testid="admin-audit-log">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{total} eventi totali</span>
        <select
          value={filterAction}
          onChange={(e) => setFilterAction(e.target.value)}
          className="bg-white border border-[#B8D4E8] text-[#0D1B2A] p-2 text-xs uppercase tracking-widest focus:border-[#FF3B30] focus:outline-none"
          data-testid="audit-filter-action"
        >
          <option value="">Tutte le azioni</option>
          {Object.keys(ACTION_META).map(a => (
            <option key={a} value={a}>{ACTION_META[a].label}</option>
          ))}
        </select>
        <button onClick={fetchLog} className="p-2 border border-[#B8D4E8] hover:border-[#FF3B30] transition-all bg-white" data-testid="audit-refresh-btn">
          <RefreshCw size={14} className="text-[#4A6B8A]" />
        </button>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <Loader2 size={20} className="animate-spin text-[#4A6B8A] mx-auto" />
        </div>
      ) : items.length === 0 ? (
        <div className="border border-[#B8D4E8] bg-white p-8 text-center">
          <Shield size={32} className="mx-auto text-[#B8D4E8] mb-2" />
          <p className="text-xs text-[#4A6B8A] uppercase tracking-widest">Nessun evento registrato</p>
          <p className="text-[10px] text-[#6B8AB5] mt-1">Le azioni admin/operatori verranno tracciate qui automaticamente.</p>
        </div>
      ) : (
        <div className="border border-[#B8D4E8] bg-white overflow-hidden">
          <table className="w-full" data-testid="audit-table">
            <thead>
              <tr className="bg-[#E8F4FD]">
                <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Quando</th>
                <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Attore</th>
                <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Azione</th>
                <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Target</th>
                <th className="text-left px-4 py-3 text-[10px] text-[#4A6B8A] uppercase tracking-widest font-bold">Dettagli</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const meta = ACTION_META[row.action] || { label: row.action, color: '#4A6B8A', Icon: Shield };
                const Icon = meta.Icon;
                return (
                  <tr key={row.id} className="border-t border-[#E8F4FD] hover:bg-[#F0F8FF]" data-testid={`audit-row-${row.id}`}>
                    <td className="px-4 py-3 text-[10px] text-[#6B8AB5] font-mono">{new Date(row.created_at).toLocaleString('it-IT')}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex items-center gap-1">
                        <User size={11} className="text-[#4A6B8A]" />
                        <span className="text-[#0D1B2A] font-bold">{row.actor_email}</span>
                      </div>
                      <span className="text-[9px] text-[#6B8AB5] uppercase tracking-widest">{row.actor_role || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1" style={{ color: meta.color }}>
                        <Icon size={12} />
                        <span className="text-[10px] font-bold uppercase tracking-widest">{meta.label}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[10px] text-[#4A6B8A]">
                      {row.target_type && (
                        <>
                          <span className="uppercase tracking-widest text-[#6B8AB5]">{row.target_type}</span>
                          <br/>
                          <span className="font-mono">{(row.target_id || '').substring(0, 36)}</span>
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[10px] text-[#4A6B8A] font-mono max-w-xs">
                      {row.details && Object.keys(row.details).length > 0 ? (
                        <pre className="whitespace-pre-wrap break-words text-[9px]">{JSON.stringify(row.details, null, 0)}</pre>
                      ) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
